import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Calendar, Target, TrendingUp, BookOpen, FileCheck, Lightbulb, Shield, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { SEO } from '../../SEO';
import { useNavigate } from 'react-router-dom';
import { UpcomingTrainingsSection } from '../../UpcomingTrainingsSection';
import { ISOProgressionPath } from '../ISOProgressionPath';
import { RelatedCoursesSection } from '../RelatedCoursesSection';

export const InternalAuditorTemplate = ({ standard, content }) => {
  const navigate = useNavigate();

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Training', path: '/training' },
    { name: 'ISO Training Services', path: '/training/iso-training-services' },
    { name: `${standard.code} Internal Auditor`, path: content.seo.canonical }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": content.seo.title,
    "description": content.seo.description,
    "url": `https://www.isde.academy${content.seo.canonical}`,
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://www.isde.academy"
    },
    "coursePrerequisites": "Basic knowledge of the standard recommended",
    "educationalLevel": "Intermediate",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": ["In-person", "Online"],
      "duration": `P${standard.trainingDuration?.internalAuditor?.charAt(0) || '3'}D`
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={content.seo.title}
        description={content.seo.description}
        keywords={content.seo.keywords}
        canonical={content.seo.canonical}
        structuredData={structuredData}
        breadcrumbs={breadcrumbs}
      />

      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${standard.color.gradient} text-white py-20 mt-20`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <FileCheck className="h-5 w-5" />
              PRACTICAL AUDIT TRAINING
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {standard.code} Internal Auditor Training
            </h1>
            <p className="text-xl md:text-2xl opacity-90 mb-8 max-w-4xl mx-auto leading-relaxed">
              {content.heroDescription}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>{standard.trainingDuration?.internalAuditor?.charAt(0).toUpperCase() + standard.trainingDuration?.internalAuditor?.slice(1) || '3 Days'} Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-6 w-6" />
                <span>Practical Exercises</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standard} currentLevel="internalAuditor" />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
              What is {standard.code} Internal Auditor Training?
            </h2>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                {content.introduction}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {content.roleOfInternalAuditor}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What You'll Master Section */}
      <section className={`py-16 bg-gradient-to-br ${standard.color.bg} to-white`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What You'll Master
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This comprehensive {standard.trainingDuration?.internalAuditor || '3-day'} training covers all aspects of internal auditing
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {content.auditSkills.map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-2 border-transparent hover:shadow-xl transition-all" style={{ borderColor: `${standard.color.border.replace('border-', '#')}40` }}>
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className={`p-4 rounded-full bg-gradient-to-br ${standard.color.gradient} mb-4`}>
                        <skill.icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {skill.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {skill.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Course Highlights
            </h3>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {content.courseHighlights.map((highlight, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className={`h-full ${standard.color.bg} border-l-4`} style={{ borderLeftColor: standard.color.text.replace('text-', '#') }}>
                  <CardContent className="p-6">
                    <h4 className={`text-xl font-bold ${standard.color.text} mb-4 flex items-center gap-2`}>
                      <highlight.icon className="h-6 w-6" />
                      {highlight.title}
                    </h4>
                    <ul className="space-y-2">
                      {highlight.points.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle className={`h-5 w-5 ${standard.color.text} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Methodology Section */}
      <section className={`py-16 bg-gradient-to-br ${standard.color.bg} to-white`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Training Methodology
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our interactive approach ensures practical learning and real-world application
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {content.methodology.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white hover:shadow-lg transition-shadow">
                  <CardContent className="p-6 text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${standard.color.gradient} mb-4`}>
                      <method.icon className="h-8 w-8 text-white" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2">{method.title}</h4>
                    <p className="text-sm text-gray-600">{method.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This training is ideal for professionals seeking to conduct internal audits
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {content.targetAudience.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className={`h-full border-l-4 ${standard.color.border} hover:shadow-lg transition-shadow`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className={`h-5 w-5 ${standard.color.text} flex-shrink-0`} />
                      <span className="text-sm font-medium text-gray-800">{role}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={`py-16 bg-gradient-to-br ${standard.color.bg} to-white`}>
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Training Benefits
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Professional Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className={`h-full border-t-4 ${standard.color.border}`}>
                <CardContent className="p-6">
                  <h4 className={`text-xl font-bold ${standard.color.text} mb-4 flex items-center gap-2`}>
                    <Award className="h-6 w-6" />
                    Professional Benefits
                  </h4>
                  <ul className="space-y-3">
                    {content.benefits.professional.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className={`h-5 w-5 ${standard.color.text} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Organizational Benefits */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className={`h-full border-t-4 ${standard.color.border}`}>
                <CardContent className="p-6">
                  <h4 className={`text-xl font-bold ${standard.color.text} mb-4 flex items-center gap-2`}>
                    <Target className="h-6 w-6" />
                    Organizational Benefits
                  </h4>
                  <ul className="space-y-3">
                    {content.benefits.organizational.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className={`h-5 w-5 ${standard.color.text} flex-shrink-0 mt-0.5`} />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certification Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className={`max-w-4xl mx-auto bg-gradient-to-br ${standard.color.gradient} text-white`}>
              <CardContent className="p-8">
                <div className="text-center">
                  <Award className="h-16 w-16 mx-auto mb-4 opacity-90" />
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Certification & Assessment
                  </h3>
                  <div className="space-y-3 text-lg opacity-90">
                    {content.certification.map((item, index) => (
                      <p key={index} className="flex items-center justify-center gap-3">
                        <CheckCircle className="h-6 w-6 flex-shrink-0" />
                        <span>{item}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug={standard.slug} />

      {/* CTA Section */}
      <section className={`py-16 bg-gradient-to-r ${standard.color.gradient} text-white`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become a Certified Internal Auditor?
            </h2>
            <p className="text-xl opacity-90 mb-8">
              Join our comprehensive {standard.trainingDuration?.internalAuditor || '3-day'} training and master {standard.code} internal auditing
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
                style={{ color: standard.color.text.replace('text-', '#') }}
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Trainings Section */}
      <UpcomingTrainingsSection
        pageSlug={`${standard.slug}-internal-auditor`}
        title={`Upcoming ${standard.code} Internal Auditor Training Sessions`}
      />
    </div>
  );
};
